import React, { useMemo } from 'react';
import { Table, Tabs, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { BonusDetailRow, DeptSummary, QueryResponse } from '../../../services/pw9337Api';

interface ResultTableProps {
  result: QueryResponse;
}

const fmtNum = (v: number | null | undefined): string => {
  if (v == null || v === 0) return '0';
  return v.toLocaleString('en-US', { maximumFractionDigits: 0 });
};

const detailColumns: ColumnsType<BonusDetailRow & { _key: string }> = [
  {
    title: '部門',
    dataIndex: 'deptCode',
    key: 'deptCode',
    width: 100,
    render: (code: string, record) =>
      `${(code || '').trim()} ${(record.deptName || '').trim()}`,
  },
  {
    title: '員工代號',
    dataIndex: 'empCode',
    key: 'empCode',
    width: 80,
  },
  {
    title: '員工姓名',
    dataIndex: 'empName',
    key: 'empName',
    width: 80,
  },
  {
    title: '本薪獎金',
    dataIndex: 'baseSalary',
    key: 'baseSalary',
    width: 100,
    align: 'right',
    render: (v: number) => fmtNum(v),
  },
  {
    title: '特別獎金',
    dataIndex: 'specialBonus',
    key: 'specialBonus',
    width: 100,
    align: 'right',
    render: (v: number) => fmtNum(v),
  },
  {
    title: '合計獎金',
    key: 'totalBonus',
    width: 100,
    align: 'right',
    render: (_: unknown, record) => fmtNum((record.baseSalary ?? 0) + (record.specialBonus ?? 0)),
  },
  {
    title: '稅率(%)',
    dataIndex: 'taxRate',
    key: 'taxRate',
    width: 70,
    align: 'right',
    render: (v: number) => (v ?? 0).toFixed(1),
  },
  {
    title: '稅額',
    dataIndex: 'taxAmount',
    key: 'taxAmount',
    width: 80,
    align: 'right',
    render: (v: number) => <Tag color="orange">{fmtNum(v)}</Tag>,
  },
  {
    title: '實領金額',
    dataIndex: 'netAmount',
    key: 'netAmount',
    width: 100,
    align: 'right',
    render: (v: number) => <Tag color="green">{fmtNum(v)}</Tag>,
  },
];

const summaryColumns: ColumnsType<DeptSummary & { _key: string }> = [
  {
    title: '部門',
    dataIndex: 'deptCode',
    key: 'deptCode',
    width: 80,
    render: (code: string, record) =>
      `${(code || '').trim()} ${(record.deptName || '').trim()}`,
  },
  {
    title: '人數',
    dataIndex: 'empCount',
    key: 'empCount',
    width: 60,
    align: 'center',
  },
  {
    title: '本薪獎金合計',
    dataIndex: 'totalBase',
    key: 'totalBase',
    width: 120,
    align: 'right',
    render: (v: number) => fmtNum(v),
  },
  {
    title: '特別獎金合計',
    dataIndex: 'totalSpecial',
    key: 'totalSpecial',
    width: 120,
    align: 'right',
    render: (v: number) => fmtNum(v),
  },
  {
    title: '稅額合計',
    dataIndex: 'totalTax',
    key: 'totalTax',
    width: 100,
    align: 'right',
    render: (v: number) => <Tag color="orange">{fmtNum(v)}</Tag>,
  },
  {
    title: '實領合計',
    dataIndex: 'totalNet',
    key: 'totalNet',
    width: 120,
    align: 'right',
    render: (v: number) => <Tag color="green">{fmtNum(v)}</Tag>,
  },
];

const ResultTable: React.FC<ResultTableProps> = ({ result }) => {
  const detailData = useMemo(
    () =>
      (result.details || []).map((row, i) => ({
        ...row,
        _key: `${row.deptCode}-${row.empCode}-${i}`,
      })),
    [result.details]
  );

  const summaryData = useMemo(
    () =>
      (result.deptSummary || []).map((row, i) => ({
        ...row,
        _key: `${row.deptCode}-${i}`,
      })),
    [result.deptSummary]
  );

  const items = [
    {
      key: 'details',
      label: `獎金明細 (${result.totalCount})`,
      children: (
        <Table
          columns={detailColumns}
          dataSource={detailData}
          rowKey="_key"
          size="small"
          scroll={{ x: 900, y: 500 }}
          pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] }}
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={3}>
                  <strong>合計</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">
                  <strong>{fmtNum(result.grandBase)}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right">
                  <strong>{fmtNum(result.grandSpecial)}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} align="right">
                  <strong>{fmtNum((result.grandBase ?? 0) + (result.grandSpecial ?? 0))}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={6} />
                <Table.Summary.Cell index={7} align="right">
                  <strong>{fmtNum(result.grandTax)}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={8} align="right">
                  <strong>{fmtNum(result.grandNet)}</strong>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      ),
    },
    {
      key: 'summary',
      label: `部門彙總 (${summaryData.length})`,
      children: (
        <Table
          columns={summaryColumns}
          dataSource={summaryData}
          rowKey="_key"
          size="small"
          scroll={{ x: 600, y: 500 }}
          pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] }}
        />
      ),
    },
  ];

  return <Tabs items={items} />;
};

export default ResultTable;
